import * as THREE from "three";
import * as global from "../../../data/global";
import {
    chainLayoutSidesLocal,
    chainTolForLayoutSides,
} from "../../../components/siteLayoutStice";

const finite = (n) => typeof n === "number" && Number.isFinite(n);

const sideHasOuter = (s) =>
    finite(s.lx1) && finite(s.lz1) && finite(s.lx2) && finite(s.lz2);

const WALL_THICKNESS_FT = 0.5;

const tryExtrudeShape = (shape, settings) => {
    try {
        return new THREE.ExtrudeGeometry(shape, settings);
    } catch {
        return null;
    }
};

/** Inset polygon toward centroid (wall inner edge), matching drawSite 0.5 ft thickness. */
const insetPolygonFt = (poly, inset) => {
    let cx = 0;
    let cz = 0;
    poly.forEach((p) => {
        cx += p.x;
        cz += p.z;
    });
    cx /= poly.length;
    cz /= poly.length;
    return poly.map((p) => {
        const dx = cx - p.x;
        const dz = cz - p.z;
        const len = Math.hypot(dx, dz);
        if (len < inset * 1.2) return { x: cx, z: cz };
        return { x: p.x + (dx / len) * inset, z: p.z + (dz / len) * inset };
    });
};

const PREVIEW_BASE_Y = -2.26;

/** Add midpoints so long straight edges are not coarse when viewed in 3D. */
const densifyPolyFt = (polyFt, maxEdgeFt = 1.5) => {
    if (!polyFt?.length) return polyFt;
    const out = [];
    for (let i = 0; i < polyFt.length; i++) {
        const a = polyFt[i];
        const b = polyFt[(i + 1) % polyFt.length];
        out.push(a);
        const len = Math.hypot(b.x - a.x, b.z - a.z);
        const steps = Math.max(1, Math.ceil(len / maxEdgeFt));
        for (let s = 1; s < steps; s++) {
            const t = s / steps;
            out.push({
                x: a.x + (b.x - a.x) * t,
                z: a.z + (b.z - a.z) * t,
            });
        }
    }
    return out;
};

/** Closed line loop in layout XZ (bin-local feet) — outline only, no fill. */
const lineLoopFromPolyFt = (polyFt, y = PREVIEW_BASE_Y) => {
    if (!polyFt?.length) return null;
    const pts = polyFt.map((p) => new THREE.Vector3(p.x, y, p.z));
    pts.push(pts[0].clone());
    return new THREE.BufferGeometry().setFromPoints(pts);
};

const WALL_THICKNESS_FT_ROUND = 0.5;

/** Segment count so each arc is ~0.35 ft (smooth circles at any bin diameter). */
export const ringSegmentsForRadiusFt = (radiusFt) => {
    const r = Math.max(radiusFt, 1);
    const circumference = 2 * Math.PI * r;
    const arcLenFt = 0.35;
    return Math.min(384, Math.max(96, Math.ceil(circumference / arcLenFt)));
};

const circleLineLoopXZ = (radiusFt, segments, y = PREVIEW_BASE_Y) => {
    const pts = [];
    for (let i = 0; i < segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        pts.push(
            new THREE.Vector3(
                radiusFt * Math.cos(a),
                y,
                radiusFt * Math.sin(a)
            )
        );
    }
    return new THREE.BufferGeometry().setFromPoints(pts);
};

/**
 * Round-bin base rings in XZ (outer + inner), same style as flat stice outlines.
 */
export const buildPreviewRoundBinOutlines = (diameterFt, wallFt = WALL_THICKNESS_FT_ROUND) => {
    const outerR = Math.max((diameterFt || 30) / 2, 1);
    const innerR = Math.max(outerR - wallFt, 0.1);
    const outerSegs = ringSegmentsForRadiusFt(outerR);
    const innerSegs = ringSegmentsForRadiusFt(innerR);
    return {
        outer: circleLineLoopXZ(outerR, outerSegs),
        inner: circleLineLoopXZ(innerR, innerSegs),
    };
};

/**
 * Layout preview stice base: outer + inner outline (0.5 ft wall), Site3d-style from above.
 */
export const buildPreviewSticeBaseOutlines = (polyFt) => {
    if (!polyFt || polyFt.length < 3) {
        return { outer: null, inner: null };
    }
    const outer = densifyPolyFt(polyFt);
    const inner = densifyPolyFt(insetPolygonFt(polyFt, WALL_THICKNESS_FT));
    return {
        outer: lineLoopFromPolyFt(outer),
        inner: lineLoopFromPolyFt(inner),
    };
};

/** Move a closed footprint so its vertex centroid sits at the origin (single-bin /bin view). */
export const centerPolygonFtAtOrigin = (polyFt) => {
    if (!polyFt?.length) return polyFt;
    let cx = 0;
    let cz = 0;
    for (const p of polyFt) {
        cx += p.x;
        cz += p.z;
    }
    cx /= polyFt.length;
    cz /= polyFt.length;
    return polyFt.map((p) => ({ x: p.x - cx, z: p.z - cz }));
};

/** Closed footprint from chained sides (corner list). */
export const polyFtFromOrderedSides = (ordered) => {
    if (!ordered?.length) return null;
    const poly = [];
    ordered.forEach((s) => {
        if (finite(s.lx1) && finite(s.lz1)) poly.push({ x: s.lx1, z: s.lz1 });
    });
    const last = ordered[ordered.length - 1];
    if (finite(last?.lx2) && finite(last?.lz2)) {
        const d = poly.length
            ? Math.hypot(poly[0].x - last.lx2, poly[0].z - last.lz2)
            : 1;
        if (d > 0.05) poly.push({ x: last.lx2, z: last.lz2 });
    }
    return poly.length >= 3 ? poly : null;
};

/** Axis-aligned rectangle from catalog width/length (feet, bin-local). */
export const rectPolyFtFromBinSize = (width, length) => {
    const hw = Math.max((width || 30) / 2, 1);
    const hl = Math.max((length || 30) / 2, 1);
    return [
        { x: -hw, z: -hl },
        { x: hw, z: -hl },
        { x: hw, z: hl },
        { x: -hw, z: hl },
    ];
};

const pushQuad = (positions, indices, a, b, c, d) => {
    const base = positions.length / 3;
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z, d.x, d.y, d.z);
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
};

/**
 * Wall ring mesh without Shape holes (avoids earcut failures on bad layout data).
 * Built in bin-local XZ with depth along -Y (matches ExtrudeGeometry + rotateX(-π/2)).
 */
export const buildWallBandBufferGeometry = (outerPoly, innerPoly, depth = 1.25) => {
    if (!outerPoly?.length || outerPoly.length < 3 || !innerPoly?.length) return null;

    const n = outerPoly.length;
    const positions = [];
    const indices = [];

    const v = (p, y) => ({ x: p.x, y, z: p.z });
    const innerAt = (i) => innerPoly[Math.min(i, innerPoly.length - 1)];

    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const o0 = outerPoly[i];
        const o1 = outerPoly[j];
        const in0 = innerAt(i);
        const in1 = innerAt(j);

        pushQuad(
            positions,
            indices,
            v(o0, 0),
            v(o1, 0),
            v(o1, -depth),
            v(o0, -depth)
        );
        pushQuad(
            positions,
            indices,
            v(in1, 0),
            v(in0, 0),
            v(in0, -depth),
            v(in1, -depth)
        );
        pushQuad(positions, indices, v(o0, 0), v(o1, 0), v(in1, 0), v(in0, 0));
        pushQuad(
            positions,
            indices,
            v(o0, -depth),
            v(in0, -depth),
            v(in1, -depth),
            v(o1, -depth)
        );
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    return geom;
};

const buildFloorFromInnerPoly = (innerPoly, depth, radialSegments) => {
    const shape = new THREE.Shape();
    shape.moveTo(innerPoly[0].x, innerPoly[0].z);
    for (let i = 1; i < innerPoly.length; i++) {
        shape.lineTo(innerPoly[i].x, innerPoly[i].z);
    }
    shape.lineTo(innerPoly[0].x, innerPoly[0].z);
    const floorGeometry = tryExtrudeShape(shape, {
        depth,
        curveSegments: radialSegments,
        bevelEnabled: false,
        bevelSegments: 1,
        steps: 1,
        bevelSize: 0,
        bevelThickness: 1,
    });
    if (floorGeometry) floorGeometry.rotateX(-Math.PI / 2);
    return floorGeometry;
};

/**
 * Site3d-style stice base from editor polygon: extruded outer ring (same as SticeArcWall).
 */
export const buildWallGeometriesFromPolygon = (
    polyFt,
    height = 1.25,
    radialSegments = 12
) => {
    if (!polyFt || polyFt.length < 3) {
        return { geometry: null, floorGeometry: null };
    }

    const outer = densifyPolyFt(polyFt);
    const inner = densifyPolyFt(insetPolygonFt(outer, WALL_THICKNESS_FT));

    let geometry = buildWallBandBufferGeometry(outer, inner, height);
    let floorGeometry = buildFloorFromInnerPoly(inner, height, radialSegments);

    if (geometry) {
        return { geometry, floorGeometry };
    }

    const extrudeSettings = {
        depth: height,
        curveSegments: radialSegments,
        bevelEnabled: false,
        bevelSegments: 1,
        steps: 1,
        bevelSize: 0,
        bevelThickness: 1,
    };

    const shape = new THREE.Shape();
    shape.moveTo(outer[0].x, outer[0].z);
    for (let i = 1; i < outer.length; i++) {
        shape.lineTo(outer[i].x, outer[i].z);
    }
    shape.lineTo(outer[0].x, outer[0].z);

    const hole = new THREE.Shape();
    hole.moveTo(inner[0].x, inner[0].z);
    for (let i = 1; i < inner.length; i++) {
        hole.lineTo(inner[i].x, inner[i].z);
    }
    hole.lineTo(inner[0].x, inner[0].z);
    shape.holes.push(hole);

    geometry = tryExtrudeShape(shape, extrudeSettings);
    if (geometry) geometry.rotateX(-Math.PI / 2);

    if (!floorGeometry) {
        floorGeometry = buildFloorFromInnerPoly(inner, height, radialSegments);
    }

    return { geometry, floorGeometry };
};

/**
 * Triangulate a closed polygon directly in the layout XZ plane (bin-local feet).
 * @deprecated Preview uses buildWallGeometriesFromPolygon (Site3d wall band) instead.
 */
export const geometryFromPreviewPolygonXZ = (poly, y = -2.26) => {
    if (!poly || poly.length < 3) return null;
    const contour = poly.map((p) => new THREE.Vector2(p.x, p.z));
    let triangles;
    try {
        triangles = THREE.ShapeUtils.triangulateShape(contour, []);
    } catch {
        return null;
    }
    if (!triangles.length) return null;

    const positions = [];
    for (const tri of triangles) {
        for (const i of tri) {
            positions.push(contour[i].x, y, contour[i].y);
        }
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geom.computeVertexNormals();
    return geom;
};

/** Closed 2D shape for layout preview footprint (bin-local feet, x/z → shape x/y). */
export const buildLayoutPreviewFootprintShape = (layoutSides) => {
    const sides = (layoutSides || []).filter(sideHasOuter);
    if (sides.length < 3) return null;

    const tol = chainTolForLayoutSides(sides);
    const ordered = chainLayoutSidesLocal(sides, tol);
    if (ordered.length < 3) return null;

    const shape = new THREE.Shape();
    let moved = false;

    ordered.forEach((side) => {
        const isArc = side.curveRadius > 0 && finite(side.lcurveX) && finite(side.lcurveZ);
        if (isArc) {
            const ser = global.getArcStartAndEndRadians(
                side.lx1,
                side.lz1,
                side.lx2,
                side.lz2,
                side.lcurveX,
                side.lcurveZ
            );
            if (!ser) {
                if (!moved) {
                    shape.moveTo(side.lx1, side.lz1);
                    moved = true;
                }
                shape.lineTo(side.lx2, side.lz2);
                return;
            }
            if (!moved) {
                shape.moveTo(side.lx1, side.lz1);
                moved = true;
            }
            shape.absellipse(
                side.lcurveX,
                side.lcurveZ,
                side.curveRadius,
                side.curveRadius,
                ser.startRadians,
                ser.endRadians,
                !side.isInner,
                0
            );
        } else {
            if (!moved) {
                shape.moveTo(side.lx1, side.lz1);
                moved = true;
            }
            shape.lineTo(side.lx2, side.lz2);
        }
    });

    if (!moved) return null;
    return shape;
};

/** Footprint geometry from a THREE.Shape (sides fallback), placed in XZ. */
export const geometryFromFootprintShapeXZ = (shape, y = -2.26) => {
    if (!shape) return null;
    const pts = shape.getPoints(24);
    if (pts.length < 3) return null;
    const poly = pts.map((p) => ({ x: p.x, z: p.y }));
    return geometryFromPreviewPolygonXZ(poly, y);
};
